import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteContactSync } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ContactSyncModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
interface DeleteContactSyncDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedContactSync: ContactSyncModel;
  contactSyncList: ContactSyncModel[];
  changeLogRoles: string[];
}

export default function DeleteContactSyncDialog({
  open,
  handleClose,
  selectedContactSync,
  contactSyncList,
  changeLogRoles,
}: DeleteContactSyncDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [deleteGroupsCheckBox, setDeleteGroupsCheckBox] = useState(true);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteContactSync(
        selectedContactSync,
        deleteGroupsCheckBox,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact sync deleted successfully!',
      });

      const newList = contactSyncList.filter(
        (contactSync) => contactSync.id !== selectedContactSync.id
      );
      queryClient.setQueryData([UserKeys.ContactSyncs], newList);
      setDeleteGroupsCheckBox(true);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this contact sync. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.contact_sync.delete_contact_sync, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.contactSync,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.DeleteContactSync.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setDeleteGroupsCheckBox(false);
        handleClose();
      }}
      title="Delete Contact Sync"
      dialogContent={
        <Box>
          <Typography>
            Do you wish to delete the contact sync{' '}
            <strong>{selectedContactSync.name}</strong>?
          </Typography>
          <FormControl sx={{ mt: 2 }} variant="standard">
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteGroupsCheckBox}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDeleteGroupsCheckBox(true);
                    } else {
                      setDeleteGroupsCheckBox(false);
                    }
                  }}
                />
              }
              label="Delete labels and contacts associated with this sync for all destination users"
            />
          </FormControl>
          {!deleteGroupsCheckBox && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              If the label used within this sync is not removed from the
              associated users, a new sync with this label name will fail
              pushing new contacts due to a duplicate label name.
            </Alert>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        color: 'error',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
